import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShowPopup, useHapticFeedback } from '@vkruglikov/react-telegram-web-app';
import { useNavigate } from 'react-router-dom';

import { CustomModal } from '../components/CustomModal';
import { BackgroundStars } from '../components/BackgroundStars';
import { NavigationBar } from '../components/NavigationBar';
import { useGameStore } from '../store/gameStore';
import { giftGameAPI, GiftCellResult, GiftGameSession } from '../services/api';

import giftBear from '../assets/gifts/Toy Bear.webp';
import giftRing from '../assets/gifts/Diamond Ring.webp';
import giftPepe from '../assets/gifts/Plush Pepe.webp';
import giftWatch from '../assets/gifts/Swiss Watch.webp';
import giftFrog from '../assets/gifts/Kissed Frog.webp';
import giftSnoop from '../assets/gifts/Snoop Dogg.webp';
import giftLiberty from '../assets/gifts/Statue of Liberty.webp';
import giftBearNY from '../assets/gifts/Bear New Year.webp';

type GameStatus = 'playing' | 'won' | 'lost';
type CellState = 'hidden' | 'safe' | 'trap' | 'gift';

const DEFAULT_GRID = 5;
const DEFAULT_TRAPS = 8;
const DEFAULT_ATTEMPTS = 3;
const GIFT_IMAGES = [giftBear, giftRing, giftWatch, giftFrog, giftSnoop, giftLiberty, giftBearNY];

const normalizeOpenedCells = (opened: unknown): Array<{ index: number; result: GiftCellResult }> => {
  if (Array.isArray(opened)) {
    return opened as Array<{ index: number; result: GiftCellResult }>;
  }
  if (typeof opened === 'string') {
    try {
      const parsed = JSON.parse(opened);
      return Array.isArray(parsed)
        ? (parsed as Array<{ index: number; result: GiftCellResult }>)
        : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const mergeOpenedCells = (
  grid: CellState[],
  openedRaw: unknown,
): Array<{ index: number; result: GiftCellResult }> => {
  const merged = new Map<number, GiftCellResult>();
  normalizeOpenedCells(openedRaw).forEach((cell) => {
    merged.set(cell.index, cell.result);
  });
  grid.forEach((state, index) => {
    if (state !== 'hidden' && !merged.has(index)) {
      merged.set(index, state);
    }
  });
  return Array.from(merged, ([index, result]) => ({ index, result }));
};

const buildGrid = (gridSize: number, openedRaw: unknown) => {
  const total = gridSize * gridSize;
  const base = Array.from({ length: total }, () => 'hidden' as CellState);
  const opened = normalizeOpenedCells(openedRaw);
  opened.forEach((cell) => {
    if (cell.index >= 0 && cell.index < total) {
      base[cell.index] = cell.result;
    }
  });
  return base;
};

const MainPage: React.FC = () => {
  const [gridSize, setGridSize] = useState(DEFAULT_GRID);
  const [attemptsLeft, setAttemptsLeft] = useState(DEFAULT_ATTEMPTS);
  const [attemptsTotal, setAttemptsTotal] = useState(DEFAULT_ATTEMPTS);
  const [grid, setGrid] = useState<CellState[]>(() => buildGrid(DEFAULT_GRID, []));

  const [gameId, setGameId] = useState<string | null>(null);
  const [commitHash, setCommitHash] = useState<string>('');
  const [status, setStatus] = useState<GameStatus>('playing');
  const [isProcessing, setIsProcessing] = useState(false);
  const [reward, setReward] = useState<number>(0);
  const [seed, setSeed] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [lastEffect, setLastEffect] = useState<{ index: number; result: GiftCellResult } | null>(null);
  const effectTimer = useRef<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const cellImages = useRef<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminBoostLoading, setIsAdminBoostLoading] = useState(false);

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    buttons?: Array<{ id: string; text: string; type?: string }>;
    onClose?: (buttonId: string) => void;
  }>({
    title: '',
    message: '',
  });

  const navigate = useNavigate();
  const gameStore = useGameStore();
  const { stars, tickets, addStars, fetchUserData, syncPendingClicks, setTickets } = gameStore;

  useEffect(() => {
    fetchUserData();
    syncPendingClicks();
  }, [fetchUserData, syncPendingClicks]);

  const telegramShowPopup = useShowPopup();
  const [telegramImpactOccurred] = useHapticFeedback();

  const showPopup = useCallback(
    (params: any) => {
      try {
        return telegramShowPopup(params);
      } catch (e) {
        return new Promise<string>((resolve) => {
          setModalConfig({
            title: params.title || 'Уведомление',
            message: params.message,
            buttons: params.buttons || [],
            onClose: (buttonId: string) => {
              setModalIsOpen(false);
              resolve(buttonId);
            },
          });
          setModalIsOpen(true);
        });
      }
    },
    [telegramShowPopup],
  );

  const impactOccurred = useCallback(
    (style: any) => {
      try {
        telegramImpactOccurred(style);
      } catch (e) {
        // ignore
      }
    },
    [telegramImpactOccurred],
  );

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info', duration = 3000) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = window.setTimeout(() => setToast(null), duration);
  }, []);

  const applySession = useCallback((session: GiftGameSession) => {
    setGameId(session.gameId);
    setGridSize(session.gridSize);
    setAttemptsLeft(session.attemptsLeft);
    setAttemptsTotal(session.attempts);
    setCommitHash(session.commitHash);
    setStatus(session.status);
    setGrid(buildGrid(session.gridSize, session.openedCells));
    cellImages.current = Array.from(
      { length: session.gridSize * session.gridSize },
      () => GIFT_IMAGES[Math.floor(Math.random() * GIFT_IMAGES.length)],
    );
  }, []);

  const startGame = useCallback(
    async (forceNew = false) => {
      if (forceNew && tickets <= 0) {
        showToast('Нет билетиков 🎟️ Выполняй задания!', 'info', 3500);
        return;
      }

      setIsProcessing(true);
      try {
        const response = await giftGameAPI.start({
          gridSize: DEFAULT_GRID,
          traps: DEFAULT_TRAPS,
          attempts: DEFAULT_ATTEMPTS,
          forceNew,
        });
        if (response?.tickets !== undefined) {
          setTickets(response.tickets);
        }
        if (response?.session) {
          applySession(response.session);
          setReward(0);
          setSeed(null);
          setClaimed(false);
        }
      } catch (error: any) {
        const apiError = error?.response?.data;
        if (apiError?.error === 'NO_TICKETS') {
          if (apiError.tickets !== undefined) {
            setTickets(apiError.tickets);
          }
          if (forceNew) {
            showToast('Нет билетиков 🎟️ Выполняй задания!', 'info', 3500);
          }
        } else {
          console.error('Failed to start gift game:', error);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [applySession, setTickets, showToast, tickets],
  );

  useEffect(() => {
    startGame(false);
  }, [startGame]);

  useEffect(() => {
    const loadAdminStatus = async () => {
      try {
        const response = await giftGameAPI.getAdminStatus();
        if (response?.success) {
          setIsAdmin(Boolean(response.isAdmin));
        }
      } catch {
        setIsAdmin(false);
      }
    };

    loadAdminStatus();
  }, []);

  // Auto-restart game 2s after it ends
  useEffect(() => {
    if (status === 'playing' || !claimed) return;
    const timer = window.setTimeout(() => {
      startGame(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [status, claimed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lastEffect) return;
    if (effectTimer.current) {
      window.clearTimeout(effectTimer.current);
    }
    effectTimer.current = window.setTimeout(() => {
      setLastEffect(null);
    }, 520);
  }, [lastEffect]);

  const handleCellClick = useCallback(
    async (index: number) => {
      if (isProcessing || status !== 'playing' || !gameId) return;
      if (grid[index] !== 'hidden') return;

      setIsProcessing(true);
      try {
        const response = await giftGameAPI.click({ gameId, cellIndex: index });
        if (!response?.success) return;

        setAttemptsLeft(response.attemptsLeft);
        setStatus(response.status);
        setGrid(buildGrid(gridSize, mergeOpenedCells(grid, response.openedCells)));

        if (response.result) {
          setLastEffect({ index, result: response.result });
          if (response.result === 'trap') {
            impactOccurred('error');
          } else if (response.result === 'gift') {
            impactOccurred('success');
          } else {
            impactOccurred('light');
          }
        }

        if (response.finished) {
          const rewardAmount = response.reward || 0;
          setReward(rewardAmount);

          if (response.status === 'won') {
            showToast(`🐸 Пепе найден! +${rewardAmount} ⭐`, 'success', 3000);
          } else if (response.status === 'lost') {
            const rewardLine = rewardAmount > 0 ? ` +${rewardAmount} ⭐` : '';
            if (response.result === 'trap') {
              showToast(`💣 Ловушка!${rewardLine}`, 'error', 3000);
            } else {
              showToast(`Попытки кончились!${rewardLine}`, 'info', 3000);
            }
          }

          if (!claimed) {
            try {
              const claimResponse = await giftGameAPI.claim({ gameId });
              if (claimResponse?.success) {
                const gained = claimResponse.reward || 0;
                if (gained > 0) {
                  addStars(gained);
                }
                setClaimed(true);
              }
            } catch (error) {
              console.error('Gift auto-claim failed:', error);
            }
          }
        }

        if (response.seed) {
          setSeed(response.seed);
        }
      } catch (error) {
        console.error('Gift game click failed:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [addStars, claimed, gameId, grid, gridSize, impactOccurred, isProcessing, showToast, status],
  );

  const handleClaimReward = useCallback(async () => {
    if (!gameId || status === 'playing' || claimed) return;
    try {
      const response = await giftGameAPI.claim({ gameId });
      if (response?.success) {
        const gained = response.reward || 0;
        if (gained > 0) {
          addStars(gained);
        }
        setClaimed(true);
        await showPopup({
          title: 'Награда получена',
          message: `+${gained} ⭐ на баланс`,
          buttons: [{ id: 'ok', type: 'default', text: 'Ок' }],
        });
      }
    } catch (error) {
      console.error('Gift claim failed:', error);
    }
  }, [addStars, claimed, gameId, showPopup, status]);

  const handleVerify = useCallback(async () => {
    if (!gameId) return;
    try {
      const response = await giftGameAPI.verify({ gameId });
      if (response?.success) {
        await showPopup({
          title: 'Проверка честности',
          message: `Commit: ${response.commitHash}\nSeed: ${response.seed}`,
          buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
        });
      }
    } catch (error) {
      console.error('Gift verify failed:', error);
    }
  }, [gameId, showPopup]);

  const handleMissionTask = useCallback(() => {
    navigate('/tasks');
  }, [navigate]);

  const handleAdminBoost = useCallback(async () => {
    if (!isAdmin || isAdminBoostLoading) return;
    setIsAdminBoostLoading(true);
    try {
      const response = await giftGameAPI.adminTopup({ stars: 50000, tickets: 50 });
      if (response?.success) {
        await fetchUserData();
        await showPopup({
          title: 'Админ-буст применён',
          message: `Баланс: ${Number(response.stars || 0).toFixed(1)} ⭐\nБилеты: ${Number(response.tickets || 0)}`,
          buttons: [{ id: 'ok', type: 'default', text: 'Ок' }],
        });
      }
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Не удалось применить буст';
      await showPopup({
        title: 'Ошибка админ-буста',
        message,
        buttons: [{ id: 'ok', type: 'default', text: 'Понятно' }],
      });
    } finally {
      setIsAdminBoostLoading(false);
    }
  }, [fetchUserData, isAdmin, isAdminBoostLoading, showPopup]);

  const progressPercent = useMemo(() => {
    if (attemptsTotal === 0) return 0;
    return Math.max(0, Math.min(100, (attemptsLeft / attemptsTotal) * 100));
  }, [attemptsLeft, attemptsTotal]);

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="app-title">SaperStars</div>
        <div className="app-score">
          <div className="score-group">
            <span className="score-value">{tickets}</span>
            <span className="score-icon">🎟️</span>
          </div>
          <div className="score-group">
            <span className="score-value">{stars.toFixed(1)}</span>
            <span className="score-icon">⭐</span>
          </div>
        </div>
      </header>

      <div className="content">
        <BackgroundStars />

        <section className="gift-game-panel">
          <div className="gift-game-header">
            <div>
              <div className="gift-game-title">🎁 Найди, где спрятан Пепе!</div>
              <div className="gift-game-subtitle">Осталось попыток: {attemptsLeft}</div>
            </div>
            <img className="gift-game-mascot" src="/img/Plush%20Pepe.webp" alt="Pepe" />
          </div>
          <div className="gift-progress">
            <div className="gift-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="gift-game-commit">Commit: {commitHash.slice(0, 12)}…</div>
        </section>

        <div
          className="gift-grid"
          style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}
        >
          {grid.map((cell, index) => {
            const effectClass =
              lastEffect && lastEffect.index === index ? `effect-${lastEffect.result}` : '';
            return (
              <button
                key={index}
                type="button"
                className={`gift-cell ${cell} ${effectClass}`}
                onClick={() => handleCellClick(index)}
                disabled={isProcessing || status !== 'playing' || cell !== 'hidden'}
                aria-label={`gift-cell-${index}`}
              >
                {cell === 'trap' && <span className="gift-cell-icon">💣</span>}
                {cell === 'gift' && (
                  <img className="gift-cell-img" src={giftPepe} alt="" draggable={false} />
                )}
                {cell === 'safe' && (
                  <img className="gift-cell-img" src={cellImages.current[index] || giftBear} alt="" draggable={false} />
                )}
              </button>
            );
          })}
        </div>

        {isAdmin && (
          <div className="gift-game-admin-tools">
            <button className="gift-game-button admin" onClick={handleAdminBoost} disabled={isAdminBoostLoading}>
              {isAdminBoostLoading ? 'Начисляю...' : 'Админ: +50 000⭐ и +50🎟️'}
            </button>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`game-toast game-toast--${toast.type}`}>
            <span>{toast.message}</span>
          </div>
        )}

        {/* Модальное окно */}
        <CustomModal
          isOpen={modalIsOpen}
          title={modalConfig.title}
          message={modalConfig.message}
          buttons={modalConfig.buttons}
          onClose={(buttonId) => modalConfig.onClose?.(buttonId)}
        />
      </div>

      <NavigationBar />
    </div>
  );
};

export default MainPage;